// src/router/index.js
import { createRouter, createWebHistory } from 'vue-router'
import axios from '@/axios'

const HomeView = () => import('../views/LandingView.vue')
const LoginView = () => import('../views/Login.vue')
const ProfileView = () => import('../views/Profile.vue')
const BranchesView = () => import('../views/BranchesView.vue')
const BranchView = () => import('../views/BranchView.vue')
const UserEdit = () => import('../views/UserEdit.vue')
const Directory = () => import('../views/DirectoryView.vue')
const Reunions = () => import('@/views/ReunionsView.vue')
const Reunion = () => import('@/views/ReunionView.vue')
const ReunionRSVP = () => import('@/views/ReunionRSVP.vue')
const Properties = () => import('@/views/PropertiesView.vue')
const PropertyView = () => import('@/views/PropertyView.vue')
const Heirlooms = () => import('@/views/HeirloomsView.vue')
const Heirloom = () => import('@/views/HeirloomView.vue')
const Store = () => import('@/views/StoreView.vue')
const StoreProduct = () => import('@/views/ProductView.vue')
const ForgotPassword = () => import('@/views/ForgotPassword.vue')
const Admin = () => import('@/views/AdminView.vue')
const Recipe = () => import('@/views/RecipeView.vue')
const Recipes = () => import('@/views/RecipesView.vue')
const Settings = () => import('@/views/UserSetting.vue')

function handleRouteResponses(to, next, responses, errorMessage) {
  const failedResponse = responses.find(response => response.status !== 200)

  if (!failedResponse) {
    return true
  }

  switch (failedResponse.status) {
    case 401:
      next({ path: '/', query: { next: to.fullPath } })
      break
    case 403:
      next('/home')
      break
    default:
      console.error(errorMessage, failedResponse)
      next(false)
      break
  }

  return false
}

async function apiFetch(url, opts = {}) {
  const { method = 'GET', headers, body, ...rest } = opts

  return axios.request({
    url,
    method,
    headers,
    data: body,
    withCredentials: true,
    validateStatus: () => true,
    ...rest
  })
}

async function apiFetchJson(url, opts = {}) {
  const res = await apiFetch(url, opts)
  return { res, data: res.data ?? null }
}

let authState = {
  checked: false,
  ok: false,
  inflight: null
}

function resetAuthState() {
  authState.checked = false
  authState.ok = false
  authState.inflight = null
}

window.addEventListener('auth-session-changed', resetAuthState)

async function authMe() {
  return apiFetch('/api/auth/me', { method: 'GET' })
}

async function authRefresh() {
  return apiFetch('/api/auth/refresh', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: {}
  })
}

async function ensureAuthed() {
  if (authState.checked && authState.ok) return true
  if (authState.inflight) return authState.inflight

  authState.inflight = (async () => {
    let res = await authMe()

    if (res.status === 200) {
      authState.checked = true
      authState.ok = true
      return true
    }

    if (res.status === 401) {
      const r2 = await authRefresh()

      if (r2.status === 200) {
        res = await authMe()

        if (res.status === 200) {
          authState.checked = true
          authState.ok = true
          return true
        }
      }
    }

    authState.checked = true
    authState.ok = false
    return false
  })()

  try {
    return await authState.inflight
  } finally {
    authState.inflight = null
  }
}

async function loadRouteData(to, next, {
  url,
  metaKey,
  errorMessage = 'Failed to load route data'
}) {
  try {
    const ok = await ensureAuthed()

    if (!ok) {
      next({ path: '/', query: { next: to.fullPath } })
      return
    }

    const resolvedUrl =
      typeof url === 'function'
        ? url(to)
        : url

    const { res, data } = await apiFetchJson(resolvedUrl)

    if (!handleRouteResponses(to, next, [res], errorMessage)) {
      return
    }

    to.meta[metaKey] = data
    next()
  } catch (err) {
    console.error(errorMessage, err)
    next(false)
  }
}

const router = createRouter({
  history: createWebHistory('/'),
  routes: [
    {
      path: '/',
      name: 'login',
      component: LoginView,
      meta: { public: true }
    },
    {
      path: '/forgot_password',
      name: 'ForgotPassword',
      component: ForgotPassword,
      meta: { public: true }
    },
    {
      path: '/reset_password',
      name: 'ResetPassword',
      component: ForgotPassword,
      meta: { public: true }
    },
    {
      path: '/home',
      name: 'home',
      component: HomeView,
      meta: { requiresAuth: true },
      beforeEnter: async (to, from, next) => {
        try {
          const ok = await ensureAuthed()

          if (!ok) {
            next({ path: '/', query: { next: to.fullPath } })
            return
          }

          const [
            worldMapResult,
            familyWebResult,
            notableFiguresResult,
            historicalPropertiesResult
          ] = await Promise.all([
            apiFetch('/api/map/get_branch_data'),
            apiFetch('/api/familytree/get_family_web_data'),
            apiFetch('/api/family_members/notable_figures'),
            apiFetch('/api/properties/historical')
          ])

          if (
            !handleRouteResponses(
              to,
              next,
              [
                worldMapResult,
                familyWebResult,
                notableFiguresResult,
                historicalPropertiesResult
              ],
              'Failed to load home data'
            )
          ) {
            return
          }

          to.meta.mapData = worldMapResult.data
          to.meta.familyWebData = familyWebResult.data
          to.meta.notableFigures = notableFiguresResult.data
          to.meta.historicalProperties = historicalPropertiesResult.data

          next()
        } catch (err) {
          console.error('Failed to load home data:', err)
          next(false)
        }
      }
    },
    {
      path: '/branches',
      name: 'branches',
      component: BranchesView,
      meta: { requiresAuth: true },
      beforeEnter: (to, from, next) => {
        return loadRouteData(to, next, {
          url: '/api/branches/',
          metaKey: 'branchesData'
        })
      }
    },
    {
      path: '/branches/:branch_id',
      name: 'branch',
      component: BranchView,
      meta: { requiresAuth: true },
      beforeEnter: (to, from, next) => {
        return loadRouteData(to, next, {
          url: to => `/api/branches/${to.params.branch_id}`,
          metaKey: 'branchData'
        })
      }
    },
    {
      path: '/family_members/',
      name: 'Directory',
      component: Directory,
      meta: { requiresAuth: true },
      beforeEnter: (to, from, next) => {
        return loadRouteData(to, next, {
          url: '/api/family_members/',
          metaKey: 'familyData'
        })
      }
    },
    {
      path: '/family_members/:user_detail_id',
      name: 'profile',
      component: ProfileView,
      meta: { requiresAuth: true },
      beforeEnter: (to, from, next) => {
        return loadRouteData(to, next, {
          url: to => `/api/family_members/${to.params.user_detail_id}`,
          metaKey: 'userData'
        })
      }
    },
    {
      path: '/family_members/:user_detail_id/edit',
      name: 'UserEdit',
      component: UserEdit,
      meta: { requiresAuth: true },
      beforeEnter: (to, from, next) => {
        return loadRouteData(to, next, {
          url: to => `/api/family_members/${to.params.user_detail_id}/edit`,
          metaKey: 'data'
        })
      }
    },
    {
      path: '/family_members/:user_detail_id?/settings',
      name: 'Settings',
      component: Settings,
      meta: { requiresAuth: true }
    },
    {
      path: '/properties',
      name: 'Properties',
      component: Properties,
      meta: { requiresAuth: true },
      beforeEnter: (to, from, next) => {
        return loadRouteData(to, next, {
          url: '/api/properties/',
          metaKey: 'propertiesData'
        })
      }
    },
    {
      path: '/properties/:property_id',
      name: 'Property',
      component: PropertyView,
      meta: { requiresAuth: true },
      beforeEnter: (to, from, next) => {
        return loadRouteData(to, next, {
          url: to => `/api/properties/${to.params.property_id}`,
          metaKey: 'propertyData',
          errorMessage: 'Failed to load property data'
        })
      }
    },
    {
      path: '/heirlooms',
      name: 'Heirlooms',
      component: Heirlooms,
      meta: { requiresAuth: true },
      beforeEnter: (to, from, next) => {
        return loadRouteData(to, next, {
          url: '/api/heirlooms',
          metaKey: 'heirloomsData',
          errorMessage: 'Failed to load heirlooms'
        })
      }
    },
    {
      path: '/heirlooms/:id',
      name: 'Heirloom',
      component: Heirloom,
      meta: { requiresAuth: true },
      beforeEnter: (to, from, next) => {
        return loadRouteData(to, next, {
          url: to => `/api/heirlooms/${to.params.id}`,
          metaKey: 'heirloomData',
          errorMessage: 'Failed to load heirloom'
        })
      }
    },
    {
      path: '/reunions',
      name: 'Reunions',
      component: Reunions,
      meta: { requiresAuth: true },
      beforeEnter: (to, from, next) => {
        return loadRouteData(to, next, {
          url: '/api/reunions',
          metaKey: 'reunionsData',
          errorMessage: 'Failed to load reunions'
        })
      }
    },
    {
      path: '/reunions/:id',
      name: 'Reunion',
      component: Reunion,
      props: (route) => ({ id: route.params.id }),
      meta: { requiresAuth: true },
      beforeEnter: async (to, from, next) => {
        try {
          const ok = await ensureAuthed()

          if (!ok) {
            next({ path: '/', query: { next: to.fullPath } })
            return
          }

          const [reunionRes, directoryRes] = await Promise.all([
            apiFetch(`/api/reunions/${to.params.id}`),
            apiFetch('/api/family_members/options')
          ])

          if (
            !handleRouteResponses(
              to,
              next,
              [reunionRes, directoryRes],
              'Failed to load reunion'
            )
          ) {
            return
          }

          to.meta.reunionData = reunionRes.data
          to.meta.familyData = directoryRes.data
          next()
        } catch (err) {
          console.error('Failed to load reunion:', err)
          next(false)
        }
      }
    },
    {
      path: '/reunions/:id/rsvp/:user_detail_id',
      name: 'ReunionRSVP',
      component: ReunionRSVP,
      props: (route) => ({
        id: route.params.id,
        user_detail_id: route.params.user_detail_id
      }),
      meta: { requiresAuth: true },
      beforeEnter: async (to, from, next) => {
        try {
          const ok = await ensureAuthed()

          if (!ok) {
            next({ path: '/', query: { next: to.fullPath } })
            return
          }

          const [reunionRes, userRes] = await Promise.all([
            apiFetch(`/api/reunions/${to.params.id}`),
            apiFetch(`/api/family_members/${to.params.user_detail_id}`)
          ])

          if (
            !handleRouteResponses(
              to,
              next,
              [reunionRes, userRes],
              'Failed to load RSVP data'
            )
          ) {
            return
          }

          const reunionData = reunionRes.data
          const userData = userRes.data
          const guestList = Array.isArray(reunionData?.guests) ? reunionData.guests : []
          const invited = guestList.some(
            (g) => (g.guest_id || g.user_detail_id) === userData.user_detail_id
          )

          to.meta.reunionData = reunionData
          to.meta.userData = userData
          to.meta.notInvited = !invited
          next()
        } catch (err) {
          console.error('Failed to load RSVP data:', err)
          next(false)
        }
      }
    },
    {
      path: '/recipes',
      name: 'Recipes',
      component: Recipes,
      props: route => ({
        recipes: route.meta.recipesData || [],
        showEmptyButton: true
      }),
      meta: { requiresAuth: true },
      beforeEnter: (to, from, next) => {
        return loadRouteData(to, next, {
          url: '/api/recipes',
          metaKey: 'recipesData',
          errorMessage: 'Failed to load recipes'
        })
      }
    },
    {
      path: '/recipes/:recipe_id',
      name: 'Recipe',
      component: Recipe,
      props: true,
      meta: { requiresAuth: true },
      beforeEnter: (to, from, next) => {
        return loadRouteData(to, next, {
          url: to => `/api/recipes/${to.params.recipe_id}`,
          metaKey: 'recipeData',
          errorMessage: 'Failed to load recipe'
        })
      }
    },
    {
      path: '/store',
      name: 'Store',
      component: Store,
      meta: { requiresAuth: true },
      beforeEnter: (to, from, next) => {
        return loadRouteData(to, next, {
          url: '/api/store/products',
          metaKey: 'storeData',
          errorMessage: 'Failed to load store data'
        })
      }
    },
    {
      path: '/store/products/:id',
      name: 'StoreProduct',
      component: StoreProduct,
      meta: { requiresAuth: true },
      beforeEnter: (to, from, next) => {
        return loadRouteData(to, next, {
          url: to => {
            const qs = new URLSearchParams()
            qs.set('source', 'printful')

            return `/api/store/products/${to.params.id}?${qs.toString()}`
          },
          metaKey: 'storeProductData',
          errorMessage: 'Failed to load store data'
        })
      }
    },
    {
      path: '/store/books/:id',
      name: 'StoreBook',
      component: StoreProduct,
      meta: { requiresAuth: true },
      beforeEnter: (to, from, next) => {
        return loadRouteData(to, next, {
          url: `/api/store/books/${to.params.id}`,
          metaKey: 'storeProductData',
          errorMessage: 'Failed to load book'
        })
      }
    },
    {
      path: '/site_admin',
      name: 'Admin',
      component: Admin,
      meta: { requiresAuth: true }
    },
    {
      path: '/store/checkout',
      name: 'store_checkout',
      component: () => import('@/views/CheckoutView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/store/checkout/success',
      name: 'store_checkout_success',
      component: () => import('@/views/CheckoutSuccessView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/properties/:id/requests/:request_id/cancel',
      name: 'property_request_cancel',
      component: () => import('@/views/PropertyRequestCancelation.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/gdpr/approve',
      name: 'GdprApprove',
      component: () => import('@/views/EmailActionView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/gdpr/reject',
      name: 'GdprReject',
      component: () => import('@/views/EmailActionView.vue'),
      meta: { requiresAuth: true }
    }
  ]
})

router.beforeEach(async (to) => {
  if (to.meta?.public) return true

  const ok = await ensureAuthed()

  if (!ok) {
    return { path: '/', query: { next: to.fullPath } }
  }

  return true
})

export default router
